import { createGlobalStyle } from 'styled-components';

/**
 * 글로벌 스타일 컴포넌트
 * - 기본 CSS 리셋
 * - 폰트, 색상 설정
 * - 테마에 따른 기본 스타일
 * - 모바일 최적화
 */
// 제네릭 타입 제거: styled-components의 ThemeProvider가 자동으로 theme을 제공함
const GlobalStyle = createGlobalStyle`
  /* CSS 리셋 */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* 
   * 폰트 로딩 주의 사항:
   * @import는 스타일 처리 지연 및 성능 문제를 일으킬 수 있으므로 사용하지 마세요.
   * 대신 index.html에 <link> 태그로 폰트를 추가하세요.
   * 예: <link href="https://fonts.googleapis.com/css2?family=Pretendard:wght@400;500;600;700&display=swap" rel="stylesheet">
   */

  /* 루트 변수 및 기본 스타일 */
  :root {
    /* 색상 변수 - 테마에 따라 JS에서 재정의 */
    --color-primary: ${({ theme }) => theme.palette.primary.main};
    --color-primary-light: ${({ theme }) => theme.palette.primary.light};
    --color-primary-dark: ${({ theme }) => theme.palette.primary.dark};
    --color-secondary: ${({ theme }) => theme.palette.secondary.main};
    --color-secondary-light: ${({ theme }) => theme.palette.secondary.light};
    --color-secondary-dark: ${({ theme }) => theme.palette.secondary.dark};
    --color-error: ${({ theme }) => theme.palette.error.main};
    --color-warning: ${({ theme }) => theme.palette.warning.main};
    --color-info: ${({ theme }) => theme.palette.info.main};
    --color-success: ${({ theme }) => theme.palette.success.main};
    --color-background: ${({ theme }) => theme.palette.background.default};
    --color-paper: ${({ theme }) => theme.palette.background.paper};
    --color-text-primary: ${({ theme }) => theme.palette.text.primary};
    --color-text-secondary: ${({ theme }) => theme.palette.text.secondary};
    --color-divider: ${({ theme }) => theme.palette.divider};
    /* 그레이 계열 */
    --color-grey-50: ${({ theme }) => theme.palette.grey[50]};
    --color-grey-100: ${({ theme }) => theme.palette.grey[100]};
    --color-grey-200: ${({ theme }) => theme.palette.grey[200]};
    --color-grey-300: ${({ theme }) => theme.palette.grey[300]};
    --color-grey-400: ${({ theme }) => theme.palette.grey[400]};
    --color-grey-500: ${({ theme }) => theme.palette.grey[500]};
    --color-grey-600: ${({ theme }) => theme.palette.grey[600]};
    --color-grey-700: ${({ theme }) => theme.palette.grey[700]};
    --color-grey-800: ${({ theme }) => theme.palette.grey[800]};
    --color-grey-900: ${({ theme }) => theme.palette.grey[900]};
    /* 간격 변수 */
    --spacing-xs: 4px;
    --spacing-sm: 8px;
    --spacing-md: 16px;
    --spacing-lg: 24px;
    --spacing-xl: 32px;
    /* border-radius */
    --radius-xs: 2px;
    --radius-sm: 4px;
    --radius-md: 6px;
    --radius-lg: 12px;
    --radius-xl: 16px;
    --radius-round: 50%;
    /* 테마 전환 애니메이션 */
    --theme-transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    /* 폰트 */
    --font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    --font-size-base: 16px;
    --font-weight-light: 400;
    --font-weight-regular: 500;
    --font-weight-bold: 700;
  }

  /* 기본 스타일 */
  html, body {
    font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 16px;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.default};
    transition: var(--theme-transition);
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    width: 100%;
  }

  /* 스크롤바 스타일링 */
  body::-webkit-scrollbar {
    width: 10px;
  }

  body::-webkit-scrollbar-track {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }

  body::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 10px;
    border: 2px solid ${({ theme }) => theme.palette.background.default};
  }

  body::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  }

  /* 버튼 및 앵커 기본 스타일 */
  button, a {
    transition: var(--theme-transition);
  }

  /* 앵커 스타일 */
  a {
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
      color: ${({ theme }) => theme.palette.primary.dark};
    }
  }

  /* 접근성 유틸리티 클래스 */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  /* 포커스 시 스크린 리더용 요소를 시각적으로 표시 - 키보드 사용자를 위한 접근성 */
  .sr-only-focusable:focus,
  .sr-only-focusable:active {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    white-space: normal;
    padding: var(--spacing-md);
    background-color: ${({ theme }) => theme.palette.background.paper};
    color: ${({ theme }) => theme.palette.text.primary};
    border: 2px solid ${({ theme }) => theme.palette.primary.main};
    z-index: 9999;
    display: block;
    outline: none;
  }

  /* 코드 블록 스타일 */
  code, pre {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 0.85em;
  }

  pre {
    padding: 1rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  /* 헤더 스타일 */
  h1, h2, h3, h4, h5, h6 {
    margin-bottom: 0.5em;
    color: ${({ theme }) => theme.palette.text.primary};
    font-weight: 600;
    line-height: 1.2;
  }

  /* 모바일 최적화 */
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
  }

  /* 기본 포커스 스타일 */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 2px;
  }

  /* 테이블 기본 스타일 */
  table {
    border-collapse: collapse;
    width: 100%;
  }

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  }

  /* 다크 모드 텍스트 선택 색상 */
  ::selection {
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(74, 108, 250, 0.7)' : 'rgba(74, 108, 250, 0.3)'};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : theme.palette.text.primary)};
  }
`;

export default GlobalStyle;
